class RequestQuoteBuilder {
  constructor(root) {
    this.root = root;
    this.searchUrl = root.dataset.searchUrl;
    this.resultsSectionId = root.dataset.resultsSectionId;
    this.minQueryLength = Number(root.dataset.minQueryLength || 2);
    this.searchInput = root.querySelector("[data-request-quote-search-input]");
    this.resultsContainer = root.querySelector("[data-request-quote-results]");
    this.resultsMeta = root.querySelector("[data-request-quote-results-meta]");
    this.selectedList = root.querySelector("[data-request-quote-selected-list]");
    this.emptyState = root.querySelector("[data-request-quote-empty]");
    this.savedList = root.querySelector("[data-request-quote-saved-list]");
    this.savedEmptyState = root.querySelector("[data-request-quote-saved-empty]");
    this.addAllSavedButton = root.querySelector("[data-add-all-saved-products]");
    this.selectedInput = root.querySelector("[data-request-quote-selected-input]");
    this.customVendorInput = root.querySelector("[data-request-quote-custom-vendor]");
    this.customReferenceInput = root.querySelector("[data-request-quote-custom-reference]");
    this.customAddButton = root.querySelector("[data-request-quote-custom-add]");
    this.customStatus = root.querySelector("[data-request-quote-custom-status]");
    this.importInput = root.querySelector("[data-request-quote-import-input]");
    this.importStatus = root.querySelector("[data-request-quote-import-status]");
    this.contactForm = root.querySelector("form");
    this.formError = root.querySelector("[data-request-quote-form-error]");
    this.formSuccessMarker = root.querySelector("[data-request-quote-form-success]");
    this.loadingLabel = root.dataset.loadingLabel || "Loading";
    this.quantityLabel = root.dataset.quantityLabel || "Quantity";
    this.targetPriceLabel = root.dataset.targetPriceLabel || "Target price";
    this.targetPricePlaceholder =
      root.dataset.targetPricePlaceholder || "e.g. 12.50 EUR";
    this.targetPriceSummaryLabel =
      root.dataset.targetPriceSummaryLabel || "Target price";
    this.savedAddLabel = root.dataset.savedAddLabel || "Add to request";
    this.savedAddAllLabel =
      root.dataset.savedAddAllLabel || "Add all saved products";
    this.savedRemoveLabel = root.dataset.savedRemoveLabel || "Remove from saved";
    this.removeLabel = root.dataset.removeLabel || "Remove";
    this.productRequiredMessage =
      root.dataset.productRequiredMessage || "Please add at least one position.";
    this.customBadgeLabel = root.dataset.customBadgeLabel || "Special item";
    this.importedBadgeLabel = root.dataset.importedBadgeLabel || "Imported";
    this.referenceSummaryLabel =
      root.dataset.referenceSummaryLabel || "Reference";
    this.searchResultsLabel = root.dataset.searchResultsLabel || "Results";
    this.searchOpenLabel = root.dataset.searchOpenLabel || "Open product";
    this.searchAddLabel = root.dataset.searchAddLabel || "Add to request";
    this.searchNoResultsLabel =
      root.dataset.searchNoResultsLabel || "No matching products found.";
    this.searchPromptLabel =
      root.dataset.searchPromptLabel || "Search by brand, item code, or product name.";
    this.searchRefineLabel =
      root.dataset.searchRefineLabel || "Broadened search is active.";
    this.customAddError =
      root.dataset.customAddError || "Please enter at least one product.";
    this.importSuccessLabel =
      root.dataset.importSuccessLabel || "Positions imported";
    this.importEmptyLabel =
      root.dataset.importEmptyLabel || "No importable positions found.";
    this.importErrorLabel =
      root.dataset.importErrorLabel || "The file could not be read.";
    this.storageKey = "techaido-request-quote-products";
    this.selectedProducts = new Map();
    this.abortController = null;
    this.searchDebounce = null;
    this.lastTrackedSearchKey = "";
    this.lastTrackedZeroResultsQuery = "";
    this.lastTrackedCustomProductValue = "";
    this.savedProductsApi = window.TechaidoSavedProducts || null;

    this.restoreSelectedProducts();
    this.applyPrefillFromUrl();
    this.renderSelectedProducts();
    this.renderSavedProducts();
    this.bindEvents();
    this.trackFormSuccess();
  }

  bindEvents() {
    if (this.searchInput) {
      this.searchInput.addEventListener("input", () => {
        const query = this.searchInput.value.trim();
        window.clearTimeout(this.searchDebounce);
        this.searchDebounce = window.setTimeout(() => {
          this.fetchResults(query);
        }, 220);
      });
    }

    if (this.customAddButton) {
      this.customAddButton.addEventListener("click", () => {
        this.addCustomProduct();
      });
    }

    [this.customVendorInput, this.customReferenceInput].forEach((input) => {
      if (!input) return;
      input.addEventListener("keydown", (event) => {
        if (event.key !== "Enter") return;
        event.preventDefault();
        this.addCustomProduct();
      });
    });

    if (this.importInput) {
      this.importInput.addEventListener("change", () => {
        this.importFromFile();
      });
    }

    this.resultsContainer.addEventListener("click", (event) => {
      const button = event.target.closest("[data-add-request-product]");
      if (!button) return;

      this.upsertSelectedProduct(
        {
          id: button.dataset.productId,
          title: button.dataset.productTitle,
          url: button.dataset.productUrl,
          vendor: button.dataset.productVendor,
          reference: button.dataset.productReference || "",
          image: button.dataset.productImage || "",
          imageAlt: button.dataset.productImageAlt || button.dataset.productTitle || "",
        },
        { source: "search_results" },
      );
    });

    this.selectedList.addEventListener("click", (event) => {
      const removeButton = event.target.closest("[data-remove-selected-product]");
      if (!removeButton) return;

      this.selectedProducts.delete(removeButton.dataset.removeSelectedProduct);
      this.afterSelectionMutation();
    });

    if (this.savedList) {
      this.savedList.addEventListener("click", (event) => {
        const addButton = event.target.closest("[data-add-saved-product]");
        if (addButton) {
          const productId = addButton.dataset.addSavedProduct;
          const savedProduct = this.getSavedProducts().find(
            (product) => String(product.id) === String(productId),
          );
          if (!savedProduct) return;

          this.upsertSelectedProduct(savedProduct, {
            source: "saved_products",
          });
          return;
        }

        const removeButton = event.target.closest("[data-remove-saved-product]");
        if (!removeButton) return;

        this.savedProductsApi?.remove(removeButton.dataset.removeSavedProduct);
      });
    }

    if (this.addAllSavedButton) {
      this.addAllSavedButton.addEventListener("click", () => {
        this.getSavedProducts().forEach((product) => {
          if (this.selectedProducts.has(String(product.id))) return;
          this.upsertSelectedProduct(product, {
            source: "saved_products_bulk",
            silent: true,
          });
        });

        this.afterSelectionMutation();
      });
    }

    window.addEventListener("techaido:saved-products-updated", () => {
      this.renderSavedProducts();
    });

    this.selectedList.addEventListener("input", (event) => {
      const quantityInput = event.target.closest("[data-selected-product-quantity]");
      if (quantityInput) {
        const product = this.selectedProducts.get(
          quantityInput.dataset.selectedProductQuantity,
        );
        if (!product) return;

        const nextQuantity = Number(quantityInput.value || 1);
        product.quantity =
          Number.isFinite(nextQuantity) && nextQuantity > 0 ? nextQuantity : 1;
        this.selectedProducts.set(product.id, product);
        this.persistSelectedProducts();
        this.renderHiddenInput();
        return;
      }

      const targetPriceInput = event.target.closest(
        "[data-selected-product-target-price]",
      );
      if (!targetPriceInput) return;

      const product = this.selectedProducts.get(
        targetPriceInput.dataset.selectedProductTargetPrice,
      );
      if (!product) return;

      product.targetPrice = targetPriceInput.value.trim();
      this.selectedProducts.set(product.id, product);
      this.persistSelectedProducts();
      this.renderHiddenInput();
    });

    if (this.contactForm) {
      this.contactForm.addEventListener("submit", (event) => {
        const selectedProducts = [...this.selectedProducts.values()];
        const hasSelectedProducts = selectedProducts.length > 0;

        if (hasSelectedProducts) {
          if (this.formError) {
            this.formError.hidden = true;
            this.formError.textContent = "";
          }

          this.trackEvent("b2b_request_submit_attempt", {
            selected_product_count: selectedProducts.length,
            selected_product_ids: selectedProducts.map((product) => product.id),
            selected_product_titles: selectedProducts.map(
              (product) => product.title,
            ),
            selected_product_vendors: [
              ...new Set(
                selectedProducts
                  .map((product) => product.vendor)
                  .filter(Boolean),
              ),
            ],
            custom_position_count: selectedProducts.filter(
              (product) => product.isCustom,
            ).length,
            imported_position_count: selectedProducts.filter(
              (product) => product.sourceType === "import",
            ).length,
            search_query: this.searchInput?.value.trim() || "",
          });
          return;
        }

        event.preventDefault();
        if (this.formError) {
          this.formError.hidden = false;
          this.formError.textContent = this.productRequiredMessage;
        }
      });
    }
  }

  async fetchResults(query) {
    if (this.abortController) {
      this.abortController.abort();
    }

    if (!query || query.length < this.minQueryLength) {
      this.resultsMeta.textContent = "";
      this.resultsContainer.innerHTML = `<div class="request-quote__empty">${this.escapeHtml(
        this.searchPromptLabel,
      )}</div>`;
      return;
    }

    this.abortController = new AbortController();
    this.resultsMeta.textContent = this.loadingLabel;

    try {
      const queries = this.buildSearchQueries(query);
      const resultSets = await Promise.all(
        queries.map((candidate) =>
          this.performSearchRequest(candidate, this.abortController.signal),
        ),
      );
      const mergedResults = this.mergeSearchResults(query, resultSets);

      this.resultsMeta.textContent = "";
      this.renderSearchResults(mergedResults.items, {
        broadened: mergedResults.broadened,
      });
      this.syncResultButtons();
      this.trackSearch(query, mergedResults.items);
      this.trackZeroResults(query, mergedResults.items.length);
    } catch (error) {
      if (error.name === "AbortError") return;
      this.resultsMeta.textContent = "";
      this.resultsContainer.innerHTML = `<div class="request-quote__empty">${this.escapeHtml(
        this.searchNoResultsLabel,
      )}</div>`;
    }
  }

  buildSearchQueries(query) {
    const trimmedQuery = (query || "").trim();
    const normalizedQuery = this.normalizeSearchText(trimmedQuery);
    const tokens = [
      ...new Set(
        normalizedQuery
          .split(" ")
          .map((token) => token.trim())
          .filter((token) => token.length >= 2),
      ),
    ];
    const numericTokens = tokens.filter((token) => /\d/.test(token));
    const textTokens = tokens.filter((token) => !/\d/.test(token));
    const candidates = [trimmedQuery];

    if (normalizedQuery && normalizedQuery !== trimmedQuery.toLowerCase()) {
      candidates.push(normalizedQuery);
    }

    if (textTokens.length && numericTokens.length) {
      candidates.push(`${textTokens[0]} ${numericTokens[0]}`);
    }

    if (textTokens.length) {
      candidates.push(textTokens[0]);
    }

    numericTokens.slice(0, 2).forEach((token) => candidates.push(token));
    tokens.slice(0, 2).forEach((token) => candidates.push(token));

    return [...new Set(candidates.map((candidate) => candidate.trim()).filter(Boolean))].slice(
      0,
      5,
    );
  }

  async performSearchRequest(query, signal) {
    const url = `${this.searchUrl}?q=${encodeURIComponent(
      query,
    )}&type=product&options[prefix]=last&section_id=${this.resultsSectionId}`;
    const response = await fetch(url, { signal });
    const markup = await response.text();
    return {
      query,
      items: this.extractSearchResults(markup),
    };
  }

  extractSearchResults(markup) {
    const wrapper = document.createElement("div");
    wrapper.innerHTML = markup;

    return [...wrapper.querySelectorAll(".request-quote__result-card")]
      .map((card) => {
        const button = card.querySelector("[data-add-request-product]");
        if (!button) return null;

        const image = card.querySelector("img");
        const typeElement = card.querySelector(".request-quote__result-type");

        return {
          id: String(button.dataset.productId || ""),
          title: button.dataset.productTitle || "",
          url: button.dataset.productUrl || "",
          vendor: button.dataset.productVendor || "",
          reference: button.dataset.productReference || "",
          type: typeElement ? typeElement.textContent.trim() : "",
          imageSrc: image?.getAttribute("src") || "",
          imageAlt: image?.getAttribute("alt") || "",
        };
      })
      .filter((item) => item?.id);
  }

  mergeSearchResults(originalQuery, resultSets) {
    const resultMap = new Map();

    resultSets.forEach((resultSet) => {
      resultSet.items.forEach((item) => {
        const score = this.scoreSearchResult(item, originalQuery);
        const existing = resultMap.get(item.id);

        if (!existing || score > existing.score) {
          resultMap.set(item.id, {
            ...item,
            score,
            matchedQueries: [resultSet.query],
          });
          return;
        }

        if (!existing.matchedQueries.includes(resultSet.query)) {
          existing.matchedQueries.push(resultSet.query);
        }
      });
    });

    const items = [...resultMap.values()].sort((left, right) => {
      if (right.score !== left.score) return right.score - left.score;
      return left.title.localeCompare(right.title);
    });

    const broadened = items.some((item) =>
      item.matchedQueries.some(
        (matchedQuery) =>
          this.normalizeSearchText(matchedQuery) !==
          this.normalizeSearchText(originalQuery),
      ),
    );

    return {
      items: items.slice(0, 24),
      broadened,
    };
  }

  scoreSearchResult(product, query) {
    const normalizedQuery = this.normalizeSearchText(query);
    const tokens = normalizedQuery
      .split(" ")
      .map((token) => token.trim())
      .filter(Boolean);
    const title = this.normalizeSearchText(product.title);
    const vendor = this.normalizeSearchText(product.vendor);
    const reference = this.normalizeSearchText(product.reference);
    const type = this.normalizeSearchText(product.type);
    let score = 0;

    if (reference === normalizedQuery) score += 240;
    if (title === normalizedQuery) score += 200;
    if (vendor === normalizedQuery) score += 120;
    if (reference.startsWith(normalizedQuery)) score += 160;
    if (title.startsWith(normalizedQuery)) score += 140;
    if (title.includes(normalizedQuery)) score += 90;
    if (reference.includes(normalizedQuery)) score += 130;
    if (vendor.includes(normalizedQuery)) score += 60;

    tokens.forEach((token) => {
      const numericToken = /\d/.test(token);

      if (reference.includes(token)) score += numericToken ? 60 : 24;
      if (title.includes(token)) score += numericToken ? 34 : 20;
      if (vendor.includes(token)) score += 14;
      if (type.includes(token)) score += 6;
    });

    const fullTokenMatch = tokens.every(
      (token) =>
        title.includes(token) ||
        vendor.includes(token) ||
        reference.includes(token),
    );

    if (fullTokenMatch) score += 80;

    return score;
  }

  renderSearchResults(items, { broadened = false } = {}) {
    if (!items.length) {
      this.resultsContainer.innerHTML = `<div class="request-quote__empty">${this.escapeHtml(
        this.searchNoResultsLabel,
      )}</div>`;
      return;
    }

    const resultsInfo = `<div class="request-quote__results-info"><strong>${items.length}</strong> ${this.escapeHtml(
      this.searchResultsLabel,
    )}</div>`;
    const listItems = items.map((item) => this.buildSearchResultMarkup(item)).join("");
    const broadenedNote = broadened
      ? `<p class="request-quote__results-note">${this.escapeHtml(
          this.searchRefineLabel,
        )}</p>`
      : "";

    this.resultsContainer.innerHTML = `
      ${resultsInfo}
      <ul class="request-quote__results-list list-unstyled">${listItems}</ul>
      ${broadenedNote}
    `;
  }

  buildSearchResultMarkup(item) {
    const imageMarkup = item.imageSrc
      ? `
        <div class="request-quote__result-media">
          <img
            src="${this.escapeAttribute(item.imageSrc)}"
            alt="${this.escapeAttribute(item.imageAlt || item.title)}"
            width="110"
            height="110"
            loading="lazy"
          >
        </div>
      `
      : '<div class="request-quote__result-media"></div>';

    const referenceMarkup = item.reference
      ? `<p class="request-quote__result-reference">${this.escapeHtml(
          this.referenceSummaryLabel,
        )}: ${this.escapeHtml(item.reference)}</p>`
      : "";
    const typeMarkup = item.type
      ? `<p class="request-quote__result-type">${this.escapeHtml(item.type)}</p>`
      : "";

    return `
      <li class="request-quote__result-card">
        ${imageMarkup}
        <div class="request-quote__result-content">
          <p class="request-quote__result-vendor">${this.escapeHtml(item.vendor || "")}</p>
          <a class="request-quote__result-title" href="${this.escapeAttribute(
            item.url,
          )}" target="_blank" rel="noopener noreferrer">
            ${this.escapeHtml(item.title)}
          </a>
          ${referenceMarkup}
          ${typeMarkup}
        </div>
        <div class="request-quote__result-actions">
          <button
            type="button"
            class="button button--primary"
            data-add-request-product
            data-product-id="${this.escapeAttribute(item.id)}"
            data-product-title="${this.escapeAttribute(item.title)}"
            data-product-url="${this.escapeAttribute(item.url)}"
            data-product-vendor="${this.escapeAttribute(item.vendor || "")}"
            data-product-reference="${this.escapeAttribute(item.reference || "")}"
            data-product-image="${this.escapeAttribute(item.imageSrc || "")}"
            data-product-image-alt="${this.escapeAttribute(item.imageAlt || item.title)}"
          >
            ${this.escapeHtml(this.searchAddLabel)}
          </button>
          <a class="button button--secondary" href="${this.escapeAttribute(
            item.url,
          )}" target="_blank" rel="noopener noreferrer">
            ${this.escapeHtml(this.searchOpenLabel)}
          </a>
        </div>
      </li>
    `;
  }

  addCustomProduct() {
    const vendor = this.customVendorInput?.value.trim() || "";
    const reference = this.customReferenceInput?.value.trim() || "";

    if (!vendor && !reference) {
      this.showStatus(this.customStatus, this.customAddError, true);
      return;
    }

    const title = [vendor, reference].filter(Boolean).join(" ").trim() || reference || vendor;
    const customProduct = {
      id: this.buildCustomId({
        sourceType: "manual",
        vendor,
        title,
        reference,
      }),
      title,
      url: "",
      vendor,
      reference,
      quantity: 1,
      targetPrice: "",
      isCustom: true,
      sourceType: "manual",
    };

    this.upsertSelectedProduct(customProduct, {
      source: "custom_position",
    });
    this.trackCustomProductInterest([vendor, reference].filter(Boolean).join(" "));
    this.hideStatus(this.customStatus);

    if (this.customVendorInput) this.customVendorInput.value = "";
    if (this.customReferenceInput) {
      this.customReferenceInput.value = "";
      this.customReferenceInput.focus();
    }
  }

  async importFromFile() {
    const file = this.importInput?.files?.[0];
    if (!file) return;

    try {
      const text = await file.text();
      const importedProducts = this.parseImportedProducts(text, file.name);

      if (!importedProducts.length) {
        this.showStatus(this.importStatus, this.importEmptyLabel, true);
        this.importInput.value = "";
        return;
      }

      importedProducts.forEach((product) => {
        this.upsertSelectedProduct(product, {
          source: "file_import",
          track: false,
          silent: true,
        });
      });

      this.afterSelectionMutation();
      this.showStatus(
        this.importStatus,
        `${this.importSuccessLabel}: ${importedProducts.length}`,
      );
      this.trackEvent("b2b_request_file_import", {
        imported_item_count: importedProducts.length,
        imported_file_name: file.name,
        page_path: window.location.pathname,
        locale: document.documentElement.lang || "",
      });
    } catch (error) {
      this.showStatus(this.importStatus, this.importErrorLabel, true);
    } finally {
      if (this.importInput) {
        this.importInput.value = "";
      }
    }
  }

  parseImportedProducts(text, fileName) {
    const lines = String(text)
      .split(/\r\n|\n|\r/)
      .map((line) => line.trim())
      .filter(Boolean);

    if (!lines.length) return [];

    const delimiter = this.detectDelimiter(lines);
    const rows = delimiter
      ? lines.map((line) => this.parseDelimitedLine(line, delimiter))
      : lines.map((line) => [line]);

    let headerMap = null;
    let startIndex = 0;

    if (rows.length && this.rowLooksLikeHeader(rows[0])) {
      headerMap = this.buildHeaderMap(rows[0]);
      startIndex = 1;
    }

    const importedProducts = [];

    for (let index = startIndex; index < rows.length; index += 1) {
      const product = this.buildImportedProduct(
        rows[index],
        headerMap,
        fileName,
      );
      if (product) importedProducts.push(product);
    }

    return importedProducts;
  }

  detectDelimiter(lines) {
    const candidates = [";", "\t", ","];
    const scores = new Map(candidates.map((candidate) => [candidate, 0]));

    lines.slice(0, 5).forEach((line) => {
      candidates.forEach((candidate) => {
        scores.set(candidate, scores.get(candidate) + line.split(candidate).length - 1);
      });
    });

    const [delimiter, score] = [...scores.entries()].sort(
      (left, right) => right[1] - left[1],
    )[0];

    return score > 0 ? delimiter : null;
  }

  parseDelimitedLine(line, delimiter) {
    const values = [];
    let current = "";
    let inQuotes = false;

    for (let index = 0; index < line.length; index += 1) {
      const char = line[index];
      const nextChar = line[index + 1];

      if (char === '"') {
        if (inQuotes && nextChar === '"') {
          current += '"';
          index += 1;
        } else {
          inQuotes = !inQuotes;
        }
        continue;
      }

      if (char === delimiter && !inQuotes) {
        values.push(current.trim());
        current = "";
        continue;
      }

      current += char;
    }

    values.push(current.trim());
    return values;
  }

  rowLooksLikeHeader(row) {
    return row.some((cell) => {
      const normalized = this.normalizeHeaderCell(cell);
      return [
        "qty",
        "quantity",
        "menge",
        "brand",
        "marke",
        "manufacturer",
        "sku",
        "article",
        "artikelnummer",
        "product",
        "produkt",
        "description",
      ].includes(normalized);
    });
  }

  buildHeaderMap(row) {
    const headerMap = {};

    row.forEach((cell, index) => {
      const normalized = this.normalizeHeaderCell(cell);

      if (
        [
          "qty",
          "quantity",
          "menge",
          "anzahl",
          "qta",
          "quantita",
        ].includes(normalized)
      ) {
        headerMap.quantity = index;
      }

      if (
        [
          "brand",
          "vendor",
          "manufacturer",
          "marke",
          "hersteller",
          "marchio",
          "produttore",
        ].includes(normalized)
      ) {
        headerMap.vendor = index;
      }

      if (
        [
          "article",
          "artikel",
          "artikelnummer",
          "itemcode",
          "code",
          "codice",
          "sku",
          "reference",
          "referenz",
          "ref",
          "productnr",
        ].includes(normalized)
      ) {
        headerMap.reference = index;
      }

      if (
        [
          "title",
          "name",
          "product",
          "produkt",
          "prodotto",
          "description",
          "descrizione",
          "bezeichnung",
        ].includes(normalized)
      ) {
        headerMap.title = index;
      }
    });

    return headerMap;
  }

  buildImportedProduct(row, headerMap, fileName) {
    const cells = row.map((cell) => String(cell || "").trim()).filter(Boolean);
    if (!cells.length) return null;

    let quantity = 1;
    let vendor = "";
    let reference = "";
    let title = "";

    if (headerMap) {
      quantity = this.parseQuantity(row[headerMap.quantity]) || 1;
      vendor = String(row[headerMap.vendor] || "").trim();
      reference = String(row[headerMap.reference] || "").trim();
      title = String(row[headerMap.title] || "").trim();
    } else if (cells.length === 1) {
      title = cells[0];
    } else if (cells.length === 2) {
      const firstQuantity = this.parseQuantity(cells[0]);
      if (firstQuantity) {
        quantity = firstQuantity;
        title = cells[1];
      } else {
        vendor = cells[0];
        reference = cells[1];
      }
    } else {
      const firstQuantity = this.parseQuantity(cells[0]);
      if (firstQuantity) {
        quantity = firstQuantity;
        vendor = cells[1] || "";
        reference = cells[2] || "";
        title = cells.slice(2).join(" ");
      } else {
        vendor = cells[0] || "";
        reference = cells[1] || "";
        title = cells.slice(2).join(" ");
      }
    }

    if (!title) {
      title = [vendor, reference].filter(Boolean).join(" ").trim() || reference || vendor;
    }

    if (!title && !vendor && !reference) return null;

    return {
      id: this.buildCustomId({
        sourceType: "import",
        vendor,
        title,
        reference,
      }),
      title,
      url: "",
      vendor,
      reference,
      quantity,
      targetPrice: "",
      isCustom: true,
      sourceType: "import",
      importFileName: fileName,
    };
  }

  parseQuantity(value) {
    const normalized = String(value || "")
      .replace(",", ".")
      .trim();
    if (!normalized) return null;

    const parsed = Number(normalized);
    if (!Number.isFinite(parsed) || parsed <= 0) return null;

    return Math.max(1, Math.round(parsed));
  }

  upsertSelectedProduct(product, options = {}) {
    const { source = "manual", track = true, silent = false } = options;
    const normalizedProduct = this.normalizeSelectedProduct(product);
    const existingProduct = this.selectedProducts.get(normalizedProduct.id);

    if (existingProduct) {
      const nextProduct = {
        ...existingProduct,
        ...normalizedProduct,
        url: existingProduct.url || normalizedProduct.url,
        vendor: existingProduct.vendor || normalizedProduct.vendor,
        reference: existingProduct.reference || normalizedProduct.reference,
        image: existingProduct.image || normalizedProduct.image,
        imageAlt: existingProduct.imageAlt || normalizedProduct.imageAlt,
        targetPrice: existingProduct.targetPrice || normalizedProduct.targetPrice,
        quantity:
          (Number(existingProduct.quantity) > 0 ? Number(existingProduct.quantity) : 1) +
          (Number(normalizedProduct.quantity) > 0 ? Number(normalizedProduct.quantity) : 1),
      };

      this.selectedProducts.set(nextProduct.id, nextProduct);
    } else {
      this.selectedProducts.set(normalizedProduct.id, normalizedProduct);
    }

    if (!silent) {
      this.afterSelectionMutation();
    }

    if (track) {
      this.trackEvent("b2b_request_add_product", {
        source,
        product_id: normalizedProduct.id,
        product_title: normalizedProduct.title,
        product_vendor: normalizedProduct.vendor || "",
        product_reference: normalizedProduct.reference || "",
        is_custom_product: normalizedProduct.isCustom,
        custom_source_type: normalizedProduct.sourceType || "",
      });
    }
  }

  normalizeSelectedProduct(product) {
    const isCustom = Boolean(product.isCustom);

    return {
      id: isCustom
        ? String(
            product.id ||
              this.buildCustomId({
                sourceType: product.sourceType || "manual",
                vendor: product.vendor,
                title: product.title,
                reference: product.reference,
              }),
          )
        : String(product.id),
      title: product.title || "",
      url: product.url || "",
      vendor: product.vendor || "",
      reference: product.reference || "",
      image: product.image || product.imageSrc || "",
      imageAlt: product.imageAlt || product.title || "",
      quantity:
        Number.isFinite(Number(product.quantity)) && Number(product.quantity) > 0
          ? Number(product.quantity)
          : 1,
      targetPrice: product.targetPrice ? String(product.targetPrice) : "",
      isCustom,
      sourceType: product.sourceType || (isCustom ? "manual" : "catalog"),
      importFileName: product.importFileName || "",
    };
  }

  afterSelectionMutation() {
    this.persistSelectedProducts();
    this.renderSelectedProducts();
    this.syncResultButtons();
  }

  renderSelectedProducts() {
    const products = [...this.selectedProducts.values()];
    this.selectedList.innerHTML = "";

    if (!products.length) {
      this.emptyState.hidden = false;
      this.renderHiddenInput();
      this.renderSavedProducts();
      return;
    }

    this.emptyState.hidden = true;

    products.forEach((product) => {
      const item = document.createElement("li");
      item.className = "request-quote__selected-item";
      item.innerHTML = `
        ${this.buildSelectedImageMarkup(product)}
        <div class="request-quote__selected-copy">
          ${product.isCustom ? `<span class="request-quote__selected-badge">${this.escapeHtml(
            this.getProductBadgeLabel(product),
          )}</span>` : ""}
          <div>
            ${this.buildSelectedTitleMarkup(product)}
          </div>
          <p>${this.escapeHtml(this.getProductMetaText(product))}</p>
        </div>
        <div class="request-quote__selected-controls">
          <label>
            <span>${this.escapeHtml(this.quantityLabel)}</span>
            <input
              type="number"
              min="1"
              step="1"
              value="${product.quantity}"
              data-selected-product-quantity="${this.escapeAttribute(product.id)}"
            >
          </label>
          <label class="request-quote__selected-target-price">
            <span>${this.escapeHtml(this.targetPriceLabel)}</span>
            <input
              type="text"
              inputmode="decimal"
              value="${this.escapeAttribute(product.targetPrice || "")}"
              placeholder="${this.escapeAttribute(this.targetPricePlaceholder)}"
              data-selected-product-target-price="${this.escapeAttribute(product.id)}"
            >
          </label>
          <button type="button" class="button button--secondary" data-remove-selected-product="${this.escapeAttribute(
            product.id,
          )}">
            ${this.escapeHtml(this.removeLabel)}
          </button>
        </div>
      `;
      this.selectedList.appendChild(item);
    });

    this.renderHiddenInput();
    this.renderSavedProducts();
  }

  buildSelectedImageMarkup(product) {
    if (!product.image) {
      return '<div class="request-quote__selected-media request-quote__selected-media--empty" aria-hidden="true"></div>';
    }

    return `
      <div class="request-quote__selected-media">
        <img
          src="${this.escapeAttribute(product.image)}"
          alt="${this.escapeAttribute(product.imageAlt || product.title)}"
          width="96"
          height="96"
          loading="lazy"
        >
      </div>
    `;
  }

  buildSelectedTitleMarkup(product) {
    if (product.url) {
      return `<a href="${this.escapeAttribute(
        product.url,
      )}" target="_blank" rel="noopener noreferrer">${this.escapeHtml(
        product.title,
      )}</a>`;
    }

    return `<span class="request-quote__selected-title">${this.escapeHtml(
      product.title,
    )}</span>`;
  }

  getProductBadgeLabel(product) {
    return product.sourceType === "import"
      ? this.importedBadgeLabel
      : this.customBadgeLabel;
  }

  getProductMetaText(product) {
    const metaParts = [];

    if (product.vendor) metaParts.push(product.vendor);
    if (product.reference && product.reference !== product.title) {
      metaParts.push(`${this.referenceSummaryLabel}: ${product.reference}`);
    }
    if (product.sourceType === "import" && product.importFileName) {
      metaParts.push(product.importFileName);
    }

    return metaParts.join(" | ");
  }

  renderSavedProducts() {
    if (!this.savedList || !this.savedEmptyState || !this.addAllSavedButton) return;

    const savedProducts = this.getSavedProducts();
    this.savedList.innerHTML = "";

    if (!savedProducts.length) {
      this.savedEmptyState.hidden = false;
      this.addAllSavedButton.hidden = true;
      return;
    }

    this.savedEmptyState.hidden = true;
    const selectableSavedProducts = savedProducts.filter(
      (product) => !this.selectedProducts.has(String(product.id)),
    );
    this.addAllSavedButton.hidden = selectableSavedProducts.length === 0;
    this.addAllSavedButton.textContent = this.savedAddAllLabel;

    savedProducts.forEach((product) => {
      const isSelected = this.selectedProducts.has(String(product.id));
      const item = document.createElement("li");
      item.className = "request-quote__saved-item";
      item.innerHTML = `
        <div class="request-quote__saved-copy">
          <a href="${this.escapeAttribute(
            product.url,
          )}" target="_blank" rel="noopener noreferrer">${this.escapeHtml(
            product.title,
          )}</a>
          <p>${this.escapeHtml(product.vendor || "")}</p>
        </div>
        <div class="request-quote__saved-controls">
          <button type="button" class="button ${
            isSelected ? "button--secondary" : "button--primary"
          }" data-add-saved-product="${this.escapeAttribute(product.id)}" ${
            isSelected ? "disabled" : ""
          }>
            ${this.escapeHtml(this.savedAddLabel)}
          </button>
          <button type="button" class="button button--secondary" data-remove-saved-product="${this.escapeAttribute(
            product.id,
          )}">
            ${this.escapeHtml(this.savedRemoveLabel)}
          </button>
        </div>
      `;
      this.savedList.appendChild(item);
    });
  }

  renderHiddenInput() {
    const lines = [...this.selectedProducts.values()].map((product) => {
      const badgePart = product.isCustom
        ? `[${this.getProductBadgeLabel(product)}] `
        : "";
      const targetPricePart = product.targetPrice
        ? ` | ${this.targetPriceSummaryLabel}: ${product.targetPrice}`
        : "";
      const vendorPart = product.vendor ? ` | ${product.vendor}` : "";
      const referencePart =
        product.reference && product.reference !== product.title
          ? ` | ${this.referenceSummaryLabel}: ${product.reference}`
          : "";
      const urlPart = product.url ? ` | ${product.url}` : "";

      return `${badgePart}${product.title} | ${this.quantityLabel}: ${product.quantity}${targetPricePart}${vendorPart}${referencePart}${urlPart}`;
    });
    this.selectedInput.value = lines.join("\n");
  }

  syncResultButtons() {
    const buttons = this.resultsContainer.querySelectorAll("[data-add-request-product]");
    buttons.forEach((button) => {
      const isSelected = this.selectedProducts.has(button.dataset.productId);
      button.disabled = isSelected;
      button.classList.toggle("button--secondary", isSelected);
      button.classList.toggle("button--primary", !isSelected);
    });
  }

  persistSelectedProducts() {
    try {
      const products = [...this.selectedProducts.values()];
      window.localStorage.setItem(this.storageKey, JSON.stringify(products));
      window.dispatchEvent(
        new CustomEvent("techaido:request-products-updated", {
          detail: {
            products,
          },
        }),
      );
    } catch (error) {
      // Ignore storage issues and keep the in-memory state.
    }
  }

  restoreSelectedProducts() {
    try {
      const saved = window.localStorage.getItem(this.storageKey);
      if (!saved) return;
      const parsed = JSON.parse(saved);
      parsed.forEach((product) => {
        if (!product?.id) return;
        this.selectedProducts.set(String(product.id), {
          id: String(product.id),
          title: product.title || "",
          url: product.url || "",
          vendor: product.vendor || "",
          reference: product.reference || "",
          image: product.image || "",
          imageAlt: product.imageAlt || "",
          quantity: Number(product.quantity) > 0 ? Number(product.quantity) : 1,
          targetPrice: product.targetPrice ? String(product.targetPrice) : "",
          isCustom: Boolean(product.isCustom),
          sourceType: product.sourceType || "catalog",
          importFileName: product.importFileName || "",
        });
      });
    } catch (error) {
      // Ignore malformed local storage and continue with an empty selection.
    }
  }

  applyPrefillFromUrl() {
    try {
      const params = new URLSearchParams(window.location.search);
      const productId = params.get("prefill_product_id");
      const productTitle = params.get("prefill_product_title");
      const productUrl = params.get("prefill_product_url");
      const productImage = params.get("prefill_product_image");

      if (!productId || !productTitle || !productUrl) return;

      const existing = this.selectedProducts.get(String(productId));
      const nextProduct = {
        id: String(productId),
        title: productTitle,
        url: productUrl,
        vendor: params.get("prefill_product_vendor") || "",
        reference: params.get("prefill_product_reference") || "",
        image: productImage || "",
        imageAlt: productTitle,
        quantity: existing?.quantity > 0 ? existing.quantity : 1,
        targetPrice: existing?.targetPrice || "",
        isCustom: false,
        sourceType: "catalog",
      };

      this.selectedProducts.set(nextProduct.id, nextProduct);
      this.persistSelectedProducts();
      this.trackEvent("b2b_request_prefill_product", {
        product_id: nextProduct.id,
        product_title: nextProduct.title,
        product_vendor: nextProduct.vendor,
      });

      if (this.searchInput && !this.searchInput.value) {
        this.searchInput.value = productTitle;
      }

      if (window.history?.replaceState) {
        params.delete("prefill_product_id");
        params.delete("prefill_product_title");
        params.delete("prefill_product_url");
        params.delete("prefill_product_vendor");
        params.delete("prefill_product_reference");
        params.delete("prefill_product_image");

        const nextQuery = params.toString();
        const nextUrl = `${window.location.pathname}${
          nextQuery ? `?${nextQuery}` : ""
        }${window.location.hash}`;
        window.history.replaceState({}, document.title, nextUrl);
      }
    } catch (error) {
      // Ignore invalid URL state and keep the current selection.
    }
  }

  trackSearch(query, items) {
    const titles = items.map((item) => item.title).filter(Boolean).slice(0, 5);
    const vendors = [...new Set(items.map((item) => item.vendor).filter(Boolean))].slice(
      0,
      5,
    );
    const searchKey = `${query}::${items.length}::${titles.join("|")}`;

    if (this.lastTrackedSearchKey === searchKey) return;
    this.lastTrackedSearchKey = searchKey;

    this.trackEvent("b2b_request_search", {
      search_query: query,
      result_count: items.length,
      top_products: titles,
      top_vendors: vendors,
    });
  }

  trackZeroResults(query, resultCount) {
    const trimmedQuery = (query || "").trim();
    if (!trimmedQuery) return;

    if (resultCount > 0) {
      if (this.lastTrackedZeroResultsQuery === trimmedQuery) {
        this.lastTrackedZeroResultsQuery = "";
      }
      return;
    }

    if (this.lastTrackedZeroResultsQuery === trimmedQuery) return;
    this.lastTrackedZeroResultsQuery = trimmedQuery;

    this.trackEvent("b2b_request_zero_results", {
      search_query: trimmedQuery,
      page_path: window.location.pathname,
      locale: document.documentElement.lang || "",
    });
  }

  trackCustomProductInterest(value) {
    const trimmedValue = String(value || "").trim();
    if (!trimmedValue) return;
    if (this.lastTrackedCustomProductValue === trimmedValue) return;

    this.lastTrackedCustomProductValue = trimmedValue;

    this.trackEvent("b2b_request_custom_product", {
      custom_product_request: trimmedValue,
      search_query: this.searchInput?.value.trim() || "",
      selected_product_count: this.selectedProducts.size,
      page_path: window.location.pathname,
      locale: document.documentElement.lang || "",
    });
  }

  trackFormSuccess() {
    if (!this.formSuccessMarker) return;

    try {
      window.localStorage.removeItem(this.storageKey);
    } catch (error) {
      // Ignore storage cleanup issues after a successful submission.
    }

    this.trackEvent("b2b_request_submit_success", {
      page_path: window.location.pathname,
      locale: document.documentElement.lang || "",
    });
  }

  getSavedProducts() {
    if (!this.savedProductsApi?.getAll) return [];
    return this.savedProductsApi.getAll().map((product) => ({
      id: String(product.id),
      title: product.title || "",
      url: product.url || "",
      vendor: product.vendor || "",
      reference: product.reference || "",
      image: product.image || "",
      imageAlt: product.imageAlt || "",
      quantity: 1,
      targetPrice: "",
      isCustom: false,
      sourceType: "catalog",
    }));
  }

  buildCustomId({ sourceType = "manual", vendor = "", title = "", reference = "" }) {
    const slug = [sourceType, vendor, title, reference]
      .map((part) => this.slugify(part))
      .filter(Boolean)
      .join("-");

    return `custom:${slug || Date.now()}`;
  }

  slugify(value) {
    return String(value || "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
  }

  normalizeSearchText(value) {
    return String(value || "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  }

  normalizeHeaderCell(value) {
    return this.normalizeSearchText(value).replace(/\s+/g, "");
  }

  showStatus(node, message, isError = false) {
    if (!node) return;
    node.hidden = false;
    node.textContent = message;
    node.classList.toggle("is-error", Boolean(isError));
  }

  hideStatus(node) {
    if (!node) return;
    node.hidden = true;
    node.textContent = "";
    node.classList.remove("is-error");
  }

  trackEvent(eventName, payload = {}) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: eventName,
      ...payload,
    });
  }

  escapeHtml(value) {
    return String(value)
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#39;");
  }

  escapeAttribute(value) {
    return this.escapeHtml(value).replaceAll("`", "&#96;");
  }
}

document.addEventListener("DOMContentLoaded", () => {
  document.querySelectorAll("[data-request-quote-builder]").forEach((root) => {
    if (!root.requestQuoteBuilder) {
      root.requestQuoteBuilder = new RequestQuoteBuilder(root);
    }
  });
});
